import { api } from "@/lib/api";

/**
 * Bus de eventos minimalista para avisar entre componentes desconectados
 * (PanicBanner vive en el Layout, Panic.jsx vive en la ruta hija) que una
 * alerta de pánico cambió de estado (atendida/resuelta), sin esperar al
 * próximo ciclo de polling. Evita que el sonido siga sonando unos segundos
 * de más después de atender un SOS.
 */
const EVENT_NAME = "panic:changed";

export function notifyPanicChanged() {
  window.dispatchEvent(new Event(EVENT_NAME));
}

export function onPanicChanged(handler) {
  window.addEventListener(EVENT_NAME, handler);
  return () => window.removeEventListener(EVENT_NAME, handler);
}

/**
 * Poller ÚNICO y compartido (a nivel de módulo, no por componente) de
 * `GET /panic?status=pendiente`. PanicBanner (siempre montado en el layout)
 * y Panic.jsx (pestaña "Pendientes") suscriben al MISMO ciclo en vez de
 * disparar cada uno su propio setInterval contra el mismo endpoint — esto
 * evita el doble polling y, sobre todo, el "acumulamiento" de pedidos: un
 * guard de `inFlight` descarta el tick si el pedido anterior todavía no
 * respondió (posible con backlogs grandes/red lenta), en vez de apilar
 * pedidos concurrentes sin control.
 */
const PENDING_POLL_MS = 6000;
let pendingTimer = null;
let pendingSubscribers = new Set();
let pendingInFlight = false;
let pendingLastData = null;

async function _fetchPending() {
  if (pendingInFlight) return; // ya hay un pedido en curso: no se apila otro
  pendingInFlight = true;
  try {
    const { data } = await api.get("/panic?status=pendiente");
    pendingLastData = data || [];
    pendingSubscribers.forEach((cb) => cb(pendingLastData));
  } catch {
    /* se reintenta en el próximo ciclo */
  } finally {
    pendingInFlight = false;
  }
}

export function subscribePendingPanic(callback) {
  pendingSubscribers.add(callback);
  if (pendingLastData) callback(pendingLastData); // último dato conocido, sin esperar el próximo ciclo
  if (!pendingTimer) {
    _fetchPending();
    pendingTimer = setInterval(_fetchPending, PENDING_POLL_MS);
  }
  return () => {
    pendingSubscribers.delete(callback);
    if (pendingSubscribers.size === 0 && pendingTimer) {
      clearInterval(pendingTimer);
      pendingTimer = null;
    }
  };
}

export function forcePendingPanicRefresh() {
  return _fetchPending();
}
